import { Application, Container, Graphics, Rectangle, Sprite, UPDATE_PRIORITY, VERSION } from 'pixi.js'
import { buildSceneModel, layoutFor } from './scene/model'
import { buildScene } from './scene/pixiScene'
import { verifyScene } from './scene/verify'
import { buildAtlas } from './scene/atlas'
import { LOAD } from './load.config'
import { createHud } from './scene/hud'
import { topUpStructures } from './scene/augment'
import { createDroneHover } from './scene/droneHover'

// The war screen's PixiJS stage. Until items A–C land it holds plain shapes:
// a static field of hexes plus whatever the current beat moves.
//
// Frame timing: onFrame runs once per ticker callback at HIGH priority,
// before the render. No frame cap: ticker.maxFPS is forced to 0 (the default),
// and the logger reads performance.now() itself, never ticker.deltaMS (that
// is clamped by minFPS and scaled by speed).

// `fixture` (optional): a rules-output fixture to draw as the war scene
// (item D). It is drawn before the first frame, so the first war-screen frame
// is the scene. Without one, the placeholder hex field is drawn.
// `isRecording()`: true while a run records (draw-call stats count only then).
export async function createStage(host, { onFrame, onFirstRender, fixture = null, topUp = false, isRecording = () => false }) {
    const app = new Application()
    await app.init({
        resizeTo: window,
        background: 0x12161b,
        antialias: true,
        autoDensity: true,
        resolution: window.devicePixelRatio || 1,
        preference: 'webgl',
        powerPreference: 'high-performance',
    })
    app.ticker.maxFPS = 0
    host.appendChild(app.canvas)

    const size = { width: app.screen.width, height: app.screen.height }
    const field = new Graphics()
    const world = new Container()
    const flash = new Graphics().rect(0, 0, 8192, 8192).fill(0xffffff)
    flash.visible = false

    // Frame strobe, shown only in video-sync runs (iOS logger validation): a
    // band of 8 cells with one lit cell that steps once per rendered frame,
    // so a 240 fps camera sees every new frame as a large change. Eight
    // positions, not two, so a skipped frame reads as a jump, not as no change.
    const STROBE_CELLS = 8
    const strobeBand = new Graphics()
    const strobeCells = []
    for (let i = 0; i < STROBE_CELLS; i++) {
        const c = new Graphics()
        c.visible = false
        strobeCells.push(c)
    }
    const strobeLayer = new Container()
    strobeLayer.addChild(strobeBand, ...strobeCells)
    strobeLayer.visible = false
    let strobeAt = 0
    const strobe = {
        show(on) {
            strobeLayer.visible = on
            strobeCells[strobeAt].visible = on
        },
        advance() {
            strobeCells[strobeAt].visible = false
            strobeAt = (strobeAt + 1) % STROBE_CELLS
            strobeCells[strobeAt].visible = true
        },
    }

    // GPU overload (hold-injection test mode only): stacked full-screen
    // translucent layers, cheap for JS, heavy fill for the GPU.
    const overloadLayer = new Container()
    overloadLayer.visible = false
    const overload = {
        setLayers(n) {
            for (const c of overloadLayer.removeChildren()) c.destroy()
            for (let i = 0; i < n; i++) {
                const g = new Graphics().rect(0, 0, 8192, 8192).fill({ color: i % 2 ? 0xff00ff : 0x00ffff, alpha: 0.02 })
                overloadLayer.addChild(g)
            }
        },
        show(on) { overloadLayer.visible = on },
    }

    // The war scene (rules output → sprites), between the field and the beat;
    // a full-screen background under it and the HUD over it.
    const bgLayer = new Container()
    const sceneLayer = new Container()
    const hudLayer = new Container()
    let scene = null
    let textures = null
    let hud = null
    let hover = null
    // `topUp` (graded scene only): structures added to reach
    // LOAD.structuresPerHive per Hive (scene/augment.js), marked and logged.
    async function showScene(fx, { topUp: wantTopUp = false } = {}) {
        clearScene()
        const layout = layoutFor(app.screen.width, app.screen.height)
        const topped = wantTopUp ? topUpStructures(fx.result) : { result: fx.result, added: [] }
        const model = buildSceneModel(topped.result, layout.R)
        textures = await buildAtlas({
            names: model.map(it => it.tex), R: layout.R, resolution: app.renderer.resolution,
            screen: { width: app.screen.width, height: app.screen.height }, renderer: app.renderer,
        })
        const bg = new Sprite(textures.get('background'))
        bg.width = app.screen.width
        bg.height = app.screen.height
        bgLayer.addChild(bg)
        const { root } = buildScene(model, { pixi: { Container, Sprite }, textures })
        root.position.set(layout.offsetX, layout.offsetY)
        sceneLayer.addChild(root)
        hover = createDroneHover(root, textures)
        hover.step(performance.now())
        hud = createHud({ app, layout, textures, fixture: fx })
        hudLayer.addChild(hud.root)
        field.visible = false
        scene = {
            name: fx.meta.name, fixture: fx, root, R: layout.R, sprites: model.length, atlas: textures.stats, textures,
            droneHover: { drones: hover.drones, frames: hover.frames, fps: hover.fps ?? null },
            loadTopUp: topped.added,
            // Distinct structures (ids repeat across the two Hives, so key by side too).
            structuresOnScreen: new Set(model.filter(it => it.kind === 'structure').map(it => `${it.side}:${it.key.split(':')[1]}`)).size,
        }
        return scene
    }
    function clearScene() {
        if (!scene) return
        hud?.destroy()
        hud = null
        hover = null
        for (const c of bgLayer.removeChildren()) c.destroy()
        scene.root.destroy({ children: true })
        textures?.destroy()
        textures = null
        scene = null
        field.visible = true
    }

    // Draw calls per frame: count WebGL draws between renders (counted only
    // while a run records).
    const draws = { current: 0, frames: 0, sum: 0, max: 0, min: Infinity }
    const gl = app.renderer.gl
    if (gl) {
        for (const fn of ['drawElements', 'drawArrays', 'drawElementsInstanced', 'drawArraysInstanced']) {
            const orig = gl[fn]
            if (typeof orig !== 'function') continue
            gl[fn] = function countedDraw(...a) { draws.current++; return orig.apply(this, a) }
        }
    }
    const frameDraws = () => {
        if (isRecording() && draws.current > 0) {
            draws.frames++
            draws.sum += draws.current
            if (draws.current > draws.max) draws.max = draws.current
            if (draws.current < draws.min) draws.min = draws.current
        }
        draws.current = 0
    }
    app.ticker.add(frameDraws, null, UPDATE_PRIORITY.INTERACTION)

    // Drones at rest: the hover loop, stepped before the render.
    app.ticker.add(() => hover?.step(performance.now()), null, UPDATE_PRIORITY.NORMAL)

    app.stage.addChild(bgLayer, field, sceneLayer, world, hudLayer, overloadLayer, strobeLayer, flash)
    app.stage.eventMode = 'none'

    function drawField() {
        size.width = app.screen.width
        size.height = app.screen.height
        field.clear()
        const r = Math.max(14, Math.min(size.width, size.height) / 22)
        const dx = r * 1.5, dy = r * Math.sqrt(3)
        for (let col = 0, x = r; x < size.width + r; col++, x += dx) {
            for (let y = col % 2 ? dy / 2 : 0; y < size.height + dy; y += dy) {
                const pts = []
                for (let k = 0; k < 6; k++) {
                    const a = (Math.PI / 3) * k
                    pts.push(x + r * 0.92 * Math.cos(a), y + r * 0.92 * Math.sin(a))
                }
                field.poly(pts).fill(y < size.height / 2 ? 0x2a3440 : 0x33402c)
            }
        }
        // Strobe band: 30–40% of the height, away from the screen edges.
        const top = size.height * 0.3, h = size.height * 0.1, w = size.width / STROBE_CELLS
        strobeBand.clear().rect(0, top, size.width, h).fill(0x000000)
        strobeCells.forEach((c, i) => c.clear().rect(i * w, top, w, h).fill(0xffffff))
    }
    drawField()
    app.renderer.on('resize', drawField)
    if (fixture) await showScene(fixture, { topUp })

    app.ticker.add(onFrame, null, UPDATE_PRIORITY.HIGH)

    // First war-screen frame: the first ticker pass after a render.
    const afterRender = () => {
        app.ticker.remove(afterRender)
        onFirstRender(performance.now())
    }
    app.ticker.add(afterRender, null, UPDATE_PRIORITY.UTILITY)

    return {
        app,
        world,
        flash,
        strobe,
        overload,
        size,
        showScene,
        clearScene,
        get scene() { return scene },
        resetDrawStats() { draws.frames = 0; draws.sum = 0; draws.max = 0; draws.min = Infinity },
        drawStats() { return draws.frames ? { frames: draws.frames, mean: Math.round((draws.sum / draws.frames) * 10) / 10, max: draws.max, min: draws.min } : null },
        hudStats() { return hud?.stats() ?? null },
        // Criterion #8 on the real display tree.
        verifyScene() { return scene ? verifyScene(scene.root, scene.fixture.result, scene.R) : null },
        // PNG (data URL) of exactly the screen (not the stage's full bounds,
        // which include idle beat shapes parked off-screen), on the scene's
        // own background colour.
        screenshot() {
            return app.renderer.extract.base64({
                target: app.stage,
                frame: new Rectangle(0, 0, app.screen.width, app.screen.height),
                resolution: app.renderer.resolution,
                clearColor: '#12161b',
            })
        },
        info() {
            return {
                pixiVersion: VERSION,
                rendererType: app.renderer.name ?? app.renderer.type,
                resolution: app.renderer.resolution,
                canvas: { width: app.canvas.width, height: app.canvas.height },
                antialias: true,
                scene: scene ? { name: scene.name, sprites: scene.sprites, R: scene.R, atlas: scene.atlas, droneHover: scene.droneHover, loadTopUp: scene.loadTopUp, structuresOnScreen: scene.structuresOnScreen, hud: hud?.stats() ?? null } : null,
                load: LOAD,
                ticker: { maxFPS: app.ticker.maxFPS, minFPS: app.ticker.minFPS, speed: app.ticker.speed },
            }
        },
        destroy() {
            app.destroy(true, { children: true })
        },
    }
}
